import { useEffect, useMemo, useState } from "react";

import {
  delayBannerText,
  pageDelay,
  scheduledEntities,
  type DataSchedule,
} from "../lib/dataDelay.mjs";
import { isSupabaseConfigured } from "../lib/supabase";
import { formatUpdatedLabel } from "../lib/tabFreshness.mjs";
import { fetchTabUpdatedAt, useTabStampVersion } from "../lib/tabFreshnessData";
import { useTenantId } from "../lib/tenant";

/** How often an open page re-checks the clock against the schedule. */
const RECHECK_MS = 60_000;

/**
 * 2026-10-05: "Data delayed: last updated <time>. Meridian has been alerted
 * and is working on it." at the top of a report (its one entity) and of the
 * Executive (every scheduled entity), when the data is older than the
 * tenant's schedule allows (dataDelay.mjs, the same rule and clock the
 * pipeline escalates on). Reads the same cached stamps as the "Updated @"
 * chip and follows it when a live refresh lands. Renders nothing while the
 * stamps are loading, when the tenant has no schedule, and when every entity
 * is on time. Says nothing about the cause.
 */
export default function DataDelayBanner(props: {
  schedule: DataSchedule | null | undefined;
  /** The page's entities; omitted = every scheduled entity (the Executive). */
  entities?: ReadonlyArray<string | null | undefined>;
}) {
  const tenantId = useTenantId();
  const version = useTabStampVersion();
  const entityKey = (props.entities ?? ["*all*"]).join(",");
  const entities = useMemo(
    () => scheduledEntities(props.schedule, props.entities ?? null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.schedule, entityKey],
  );
  const [stamps, setStamps] = useState<{ key: string; at: Record<string, Date | null> } | null>(
    null,
  );
  const [now, setNow] = useState(() => new Date());

  const readKey = tenantId ? `${tenantId}|${entities.join(",")}` : null;
  useEffect(() => {
    if (!readKey || !tenantId || entities.length === 0 || !isSupabaseConfigured()) return;
    let cancelled = false;
    void Promise.all(entities.map((e) => fetchTabUpdatedAt(tenantId, e))).then((answers) => {
      if (cancelled) return;
      const at: Record<string, Date | null> = {};
      entities.forEach((e, i) => {
        at[e] = answers[i]?.at ?? null;
      });
      setStamps({ key: readKey, at });
      setNow(new Date());
    });
    return () => {
      cancelled = true;
    };
  }, [readKey, tenantId, entities, version]);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), RECHECK_MS);
    return () => clearInterval(id);
  }, []);

  if (!stamps || stamps.key !== readKey) return null;
  const delay = pageDelay(props.schedule, stamps.at, now);
  if (!delay) return null;
  const text = delayBannerText(formatUpdatedLabel(delay.lastUpdated, { now }));
  return (
    <div
      role="status"
      data-data-delayed="true"
      style={{
        margin: "12px 16px",
        padding: "10px 14px",
        borderRadius: 6,
        border: "1px solid #E8B44C",
        background: "#FFF6E0",
        color: "#5C4100",
        fontSize: 14,
        lineHeight: 1.4,
      }}
    >
      {text}
    </div>
  );
}
